import { useContext, useState } from 'react';
import './login.scss';
import { signInWithEmailAndPassword, getAuth } from 'firebase/auth';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CircularProgress from '@mui/material/CircularProgress';

const Login = () => {
  const [error, setError] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const { dispatch } = useContext(AuthContext);
  const auth = getAuth();
  const db = getFirestore();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(false);
    setErrorMessage('');
    setIsLoading(true);

    try {
      console.log('Attempting login with:', email);

      // Authenticate with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      console.log('Firebase Auth successful:', userCredential.user.uid);

      // Check if user exists in staff collection
      const staffDocRef = doc(db, 'staff', userCredential.user.uid);
      const staffDoc = await getDoc(staffDocRef);
      console.log('Staff doc exists:', staffDoc.exists());

      if (staffDoc.exists()) {
        // The auth state listener will handle the login state update
        // Just navigate to home page
        console.log('User authorized, navigating to home');
        setIsLoading(false);
        navigate('/');
      } else {
        console.log('User not found in staff collection');
        await auth.signOut();
        setErrorMessage('Access denied. Only staff members can login.');
        setError(true);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Login error:', error);
      let errorMsg = 'An error occurred during login. Please try again.';

      if (
        error.code === 'auth/wrong-password' ||
        error.code === 'auth/user-not-found'
      ) {
        errorMsg = 'Wrong email or password!';
      } else if (error.code === 'auth/too-many-requests') {
        errorMsg = 'Too many failed login attempts. Please try again later.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMsg = 'Network error. Please check your internet connection.';
      }

      setErrorMessage(errorMsg);
      setError(true);
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login">
      <div className="login-background">
        <div className="background-overlay"></div>
      </div>

      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="bank-icon">
              <AccountBalanceIcon />
            </div>
            <h1>Welcome Back</h1>
            <div className="company-info">
              <h2>Kwahu Praso Rural Bank</h2>
              <p>Shareholder's Management System</p>
            </div>
          </div>

          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <div className="input-container">
                <EmailIcon className="input-icon" />
                <input
                  type="email"
                  placeholder="Enter your email address"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  className={error ? 'error' : ''}
                />
              </div>
            </div>

            <div className="form-group">
              <div className="input-container">
                <LockIcon className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  className={error ? 'error' : ''}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                  disabled={isLoading}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </button>
              </div>
            </div>

            {error && (
              <div className="error-message">
                <span>{errorMessage}</span>
              </div>
            )}

            <button type="submit" disabled={isLoading} className="login-button">
              {isLoading ? (
                <>
                  <CircularProgress size={20} color="inherit" />
                  <span>Signing in...</span>
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="login-footer">
            <p>Secure access to your banking dashboard</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
